import { Link } from "react-router-dom";
import { MEDIA, REAL_PHOTOS } from "@/lib/church";
import { ArrowRight, Cross, Users, Heart } from "@phosphor-icons/react";

export default function About() {
    return (
        <div data-testid="about-page" className="bg-white">
            {/* HERO */}
            <section className="relative min-h-[70vh] flex items-end overflow-hidden bg-[#4C1D95]">
                <div className="absolute inset-0">
                    <img src={MEDIA.congregation} alt="Congregation" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#4C1D95]/95 via-[#4C1D95]/70 to-[#4C1D95]/30" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#4C1D95]/80 via-transparent to-transparent" />
                </div>
                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 w-full">
                    <p className="overline text-[#FFC72C] mb-4 animate-fade-up">Our Story</p>
                    <h1 className="font-display text-6xl sm:text-7xl lg:text-7xl font-bold tracking-tight text-white leading-[0.85] max-w-5xl animate-fade-up animate-delay-100" data-testid="about-title">
                        A people of <span className="text-[#FFC72C] italic">faith</span>,<br />
                        hope, and love.
                    </h1>
                    <p className="mt-8 text-lg lg:text-xl text-white/85 max-w-2xl leading-relaxed animate-fade-up animate-delay-200">
                        Greater Mount Olive has stood as a beacon of worship, service, and community in West Philadelphia for generations — rooted in the rich African Methodist Episcopal tradition.
                    </p>
                </div>
            </section>

            {/* HISTORY */}
            <section className="bg-white py-20 lg:py-28">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                    <div className="lg:col-span-7 space-y-6 text-base lg:text-lg text-slate-600 leading-relaxed">
                        <p className="overline text-[#4C1D95]">Our History</p>
                        <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight text-[#2E1A47] leading-[0.9]">
                            Generations of faith on <span className="text-[#4C1D95]">50th Street</span>.
                        </h2>
                        <p>Born from the same prophetic tradition as the founding of the African Methodist Episcopal Church under Bishop Richard Allen in Philadelphia in 1816, Greater Mount Olive has been a spiritual home for Black families across generations.</p>
                        <p>Our doors have opened through times of joy and seasons of struggle. Through worship, education, and service, we have remained a faithful witness on the corner of North 50th and Aspen, a place where God&rsquo;s people gather to praise, learn, and love.</p>
                        <p>Today we are a multigenerational community of believers — young and old, lifelong members and curious visitors — united in our commitment to live out the gospel in our neighborhood and beyond.</p>
                    </div>
                    <div className="lg:col-span-5 zoom-on-hover">
                        <img src={REAL_PHOTOS.CHURCH_EXTERIOR} alt="Greater Mount Olive AME Church — 439 N. 50th Street, Philadelphia" className="w-full aspect-[4/5] object-cover" data-testid="church-exterior-photo" />
                    </div>
                </div>
            </section>

            {/* AME IDENTITY — bold royal blue with holographic logo */}
            <section className="bg-[#4C1D95] text-white py-20 lg:py-28 relative overflow-hidden">
                <div className="absolute -right-32 -top-32 w-[500px] h-[500px] bg-[#FFC72C]/10 blur-3xl rounded-full" />
                {/* AME Heritage Shield — official transparent emblem */}
                <div className="hidden md:block absolute right-8 lg:right-16 top-1/2 -translate-y-1/2 pointer-events-none select-none z-0" data-testid="ame-crest">
                    <img src="/photos/ame-shield.png?v=2" alt="" className="w-[220px] lg:w-[280px] h-auto" />
                </div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <p className="overline text-[#FFC72C] mb-4">AME Identity</p>
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight leading-[0.9] mb-12 max-w-4xl">
                        God Our Father. Christ Our <span className="text-[#FFC72C]">Redeemer</span>. Humankind Our Family.
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl">
                        {[
                            { icon: Cross, title: "Founded 1816", text: "By Bishop Richard Allen in Philadelphia — birthplace of the AME Church and our spiritual home." },
                            { icon: Heart, title: "Wesleyan Roots", text: "Grounded in Methodist theology with a deep commitment to social holiness and justice." },
                            { icon: Users, title: "Connectional Church", text: "A global family of believers across 39 episcopal districts and millions of members worldwide." },
                        ].map((item) => (
                            <div key={item.title} className="border-t-4 border-[#FFC72C] pt-6">
                                <item.icon size={36} weight="duotone" className="text-[#FFC72C] mb-4" />
                                <h3 className="font-display text-2xl lg:text-3xl font-bold tracking-tight mb-3">{item.title}</h3>
                                <p className="text-white/85 leading-relaxed">{item.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* LEADERSHIP */}
            <section className="bg-[#F4F7F9] py-20 lg:py-28">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <p className="overline text-[#4C1D95] mb-4">Leadership</p>
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight text-[#2E1A47] leading-[0.9] mb-12 max-w-4xl">
                        Faithful shepherds.<br />
                        <span className="text-[#4C1D95]">Devoted servants.</span>
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {[
                            { name: "Rev. Marlo Woods, M.Div.", role: "Senior Pastor" },
                            { name: "Steward Board", role: "Spiritual & Administrative Officers" },
                            { name: "Trustee Board", role: "Temporal, Legal & Property Affairs" },
                            { name: "Class Leaders", role: "Discipleship & Care" },
                            { name: "Lay Council", role: "Member Engagement" },
                            { name: "Music Department", role: "Worship Leadership" },
                            { name: "President's Council", role: "Unified Ministry Leadership" },
                            { name: "Stewardess Board", role: "Communion & Worship Care" },
                        ].map((leader, i) => (
                            <div key={leader.name} className={`p-8 ${i % 3 === 1 ? 'bg-[#FFC72C] text-[#2E1A47]' : 'bg-white text-[#2E1A47]'}`} data-testid={`leader-${leader.name.replace(/\s+/g, '-').toLowerCase()}`}>
                                <p className={`overline mb-2 ${i % 3 === 1 ? '' : 'text-[#4C1D95]'}`}>{leader.role}</p>
                                <h3 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">{leader.name}</h3>
                                {leader.desc && (
                                    <p className={`mt-4 text-sm leading-relaxed ${i % 3 === 1 ? 'text-[#2E1A47]/85' : 'text-[#2E1A47]/75'}`}>{leader.desc}</p>
                                )}
                            </div>
                        ))}
                    </div>
                    <div className="mt-12">
                        <Link to="/pastor" data-testid="meet-pastor-link" className="inline-flex items-center gap-2 bg-[#4C1D95] hover:bg-[#3B0F75] text-white px-8 py-4 text-xs font-black uppercase tracking-widest transition-colors">
                            Meet Pastor Woods <ArrowRight size={14} weight="bold" />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
